import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_MY_TASKS, USE_MOCKS } from '@/mocks/fixtures'
import type {
  MyTaskApprovalItem,
  MyTaskRecordItem,
  MyTaskSection,
  MyTaskSectionKey,
  MyTasks,
} from '@/types/domain'

/* «Мои задачи» — GET /api/my-tasks (S-2b.1; the finance sections, S-2b.2).
 *
 * Served by the read composer (backend/cmd/asa/readmodel_tasks.go), session
 * only, no parameters: which sections a reader holds, and which records are
 * "mine" in each, is decided in SQL by capability, row scope and ownership or
 * team membership — and the two finance sections by two capabilities each
 * (`finance.receivables.read` with `finance.rate.approve` or
 * `finance.payment.confirm`). Nothing is filtered here, and nothing here could
 * widen it.
 *
 * Re-read on every mount (`refetchOnMount: 'always'`) and, by the client's
 * default, on window focus — no polling. The list goes stale by OTHER people's
 * acts (a colleague asks for a price, a quote's validity runs out at midnight),
 * which no mutation in this tab can name. The notification stream marks the
 * key stale on the topics that move a section (`queriesMovedByTopic`).
 */

/** The wire, before this build has decided what it can render: an item's
 *  fields are trusted only once `myTasksFromWire` has matched its kind to its
 *  section. */
interface MyTasksWire {
  readAt: string
  sections: Array<{
    key: string
    count: number
    items: Array<{ kind: string; [field: string]: unknown }>
  }>
}

/** The section keys this build can title, in the order the server serves
 *  them: the eight record sections — `expiring-soon` (FR-16) right after
 *  `awaiting-client`, under the same gate, and `case-truck-double-count`
 *  (S-12) right after `case-no-actuals`, under its gate — then the two
 *  finance ones (S-2b.2). Spelled once: `isSectionKey` reads it, and the catalogues carry
 *  one title per entry (`myTasks.sections.*`). */
export const MY_TASK_SECTION_KEYS: readonly MyTaskSectionKey[] = [
  'pricing-requested',
  'pricing-ready',
  'awaiting-client',
  'expiring-soon',
  'case-no-legs',
  'case-no-vehicle',
  'case-no-actuals',
  'case-truck-double-count',
  'rate-approvals',
  'payments-to-confirm',
]

function isSectionKey(key: string): key is MyTaskSectionKey {
  return (MY_TASK_SECTION_KEYS as readonly string[]).includes(key)
}

function isRecordItem(item: { kind: string }): item is MyTaskRecordItem {
  return item.kind === 'quote' || item.kind === 'case'
}

function isApprovalItem(item: { kind: string }): item is MyTaskApprovalItem {
  return item.kind === 'approval'
}

/** One wire section, narrowed by its key: the key decides which item kind it
 *  may hold. `payments-to-confirm` is a count and never holds a row. */
function sectionFromWire(
  key: MyTaskSectionKey,
  count: number,
  items: ReadonlyArray<{ kind: string }>,
): MyTaskSection {
  switch (key) {
    case 'rate-approvals':
      return { key, count, items: items.filter(isApprovalItem) }
    case 'payments-to-confirm':
      return { key, count, items: [] }
    default:
      return { key, count, items: items.filter(isRecordItem) }
  }
}

/** Narrows the wire to what this build can render.
 *
 *  A section whose key it cannot title is DROPPED, not drawn untitled: a
 *  server newer than this tab may add one, and a reload brings it. An item is
 *  kept only when its kind belongs to its section — a quote or a case in a
 *  record section, an approval in `rate-approvals` — because a row of the
 *  wrong kind would link to the wrong page; anything else is dropped the same
 *  way. The section keeps its served `count`, so the remainder line still says
 *  how many records wait. The served order is kept. */
export function myTasksFromWire(wire: MyTasksWire): MyTasks {
  const sections: MyTaskSection[] = []
  for (const section of wire.sections) {
    if (!isSectionKey(section.key)) continue
    sections.push(sectionFromWire(section.key, section.count, section.items))
  }
  return { readAt: wire.readAt, sections }
}

export function useMyTasks() {
  return useQuery<MyTasks>({
    queryKey: queryKeys.tasks.mine(),
    queryFn: async () =>
      USE_MOCKS ? MOCK_MY_TASKS : myTasksFromWire(await api.get<MyTasksWire>('/my-tasks')),
    refetchOnMount: 'always',
  })
}
